import { Link, useNavigate } from "react-router-dom";
import {
  BarChart2,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FilePlus2,
  FileText,
  ListChecks,
  Receipt,
  TrendingUp,
  Wallet,
  XCircle,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useClaimsForApproval, useClaimsStats, useMyClaims } from "../hooks";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import type { ClaimType, ExpenseClaimSummary } from "../types";

// ─── constants ────────────────────────────────────────────────────────────────

/** Number of recent items shown per section on the dashboard */
const DASHBOARD_RECENT_COUNT = 3;
/** Fetch size for recent-list queries (not stats — those use the /stats endpoint) */
const DASHBOARD_LIST_SIZE = 10;

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return `KES ${n.toLocaleString()}`;
}

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function typeLabel(t: ClaimType) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function ClaimsDashboardPage() {
  const navigate = useNavigate();
  const { hasRole, hasAnyRole, hasPermission } = useAuth();

  const isAdmin    = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const isApprover = isAdmin || hasPermission("HR_CLAIM_APPROVE") || hasPermission("HR_CLAIM_MANAGE") || hasPermission("FINANCE_CLAIM_APPROVE");
  // HR_CLAIM_VIEW_ALL grants access to org-wide stats + all-claims list (V87)
  const canViewAll = isAdmin || hasPermission("HR_CLAIM_VIEW_ALL");

  // Stats come from the dedicated endpoint — no large list fetches needed
  const statsQuery    = useClaimsStats();
  const stats         = statsQuery.data;

  // Small recent-lists for the dashboard panels
  const myClaims      = useMyClaims({ page: 0, size: DASHBOARD_LIST_SIZE });
  const approvalInbox = useClaimsForApproval(isApprover ? { page: 0, size: DASHBOARD_LIST_SIZE } : undefined);

  // Larger fetch to compute per-status amounts when the stats endpoint doesn't return them
  const allMineQuery  = useMyClaims({ page: 0, size: 500 });
  const allMine       = allMineQuery.data?.content ?? [];

  const mine  = myClaims.data?.content      ?? [];
  const inbox = approvalInbox.data?.content ?? [];

  const recentMine  = mine.slice(0, DASHBOARD_RECENT_COUNT);
  const recentInbox = inbox.slice(0, DASHBOARD_RECENT_COUNT);

  // Per-status amount aggregation — uses stats endpoint values when non-zero (backend supports them),
  // otherwise falls back to summing the claims list (handles backends that only return totalAmountMine).
  function sumStatus(status: string) {
    return allMine
      .filter((c) => c.status === status)
      .reduce((acc, c) => acc + (c.totalAmount ?? 0), 0);
  }
  const pendingAmt  = (stats?.pendingAmountMine  ?? 0) || sumStatus("PENDING_APPROVAL");
  const approvedAmt = (stats?.approvedAmountMine ?? 0) || sumStatus("APPROVED");
  const paidAmt     = (stats?.paidAmountMine     ?? 0) || sumStatus("PAID");
  const rejectedAmt = (stats?.rejectedAmountMine ?? 0) || sumStatus("REJECTED");
  const totalAmt    = stats?.totalAmountMine ?? allMine.reduce((acc, c) => acc + (c.totalAmount ?? 0), 0);

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Claims</h1>
        </div>
        <Link
          to="/claims/new"
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-emerald-800"
        >
          <FilePlus2 className="h-4 w-4" />
          New Claim
        </Link>
      </div>

      {/* ── My stat chips ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatChip
          label="Drafts"
          value={statsQuery.isLoading ? null : (stats?.draftMine ?? 0)}
          tone="slate"
          to="/claims/mine"
        />
        <StatChip
          label="Pending Approval"
          value={statsQuery.isLoading ? null : (stats?.pendingMine ?? 0)}
          tone="amber"
          to="/claims/mine"
          sub={stats && stats.pendingMine > 0 ? fmt(stats.pendingAmountMine) : undefined}
        />
        <StatChip
          label="Approved"
          value={statsQuery.isLoading ? null : (stats?.approvedMine ?? 0)}
          tone="emerald"
          to="/claims/mine"
          sub={stats && stats.approvedMine > 0 ? fmt(stats.approvedAmountMine) : undefined}
        />
        <StatChip
          label="Paid"
          value={statsQuery.isLoading ? null : (stats?.paidMine ?? 0)}
          tone="indigo"
          to="/claims/mine"
        />
      </div>

      {/* ── All Claims Overview — top-level, requires HR_CLAIM_VIEW_ALL (V87) ── */}
      {canViewAll && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="flex items-center justify-between border-b bg-slate-50 px-5 py-3">
            <div>
              <h2 className="font-black text-slate-900">All Claims Overview</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {stats ? `${stats.totalAll} total claims · ${fmt(stats.totalAmountAll)}` : "Loading…"}
              </p>
            </div>
            <Link to="/claims/admin" className="text-xs font-bold text-emerald-700 hover:underline">
              Manage all →
            </Link>
          </div>

          <div className="grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            {/* Status breakdown */}
            <div className="px-5 py-4">
              {statsQuery.isLoading ? (
                <p className="text-sm text-slate-400">Loading org-wide stats…</p>
              ) : stats ? (
                <>
                  <div className="mb-2 flex flex-wrap gap-3 text-[10px] font-bold">
                    {[
                      { label: "Pending",  count: stats.pendingAll,  color: "bg-amber-400" },
                      { label: "Approved", count: stats.approvedAll, color: "bg-emerald-500" },
                      { label: "Rejected", count: stats.rejectedAll, color: "bg-rose-400" },
                      { label: "Paid",     count: stats.paidAll,     color: "bg-indigo-500" },
                    ].filter((s) => s.count > 0).map((s) => (
                      <span key={s.label} className="inline-flex items-center gap-1 text-slate-600">
                        <span className={`inline-block h-2 w-2 rounded-full ${s.color}`} />
                        {s.label} ({s.count})
                      </span>
                    ))}
                  </div>
                  <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                    {stats.totalAll > 0 && [
                      { count: stats.pendingAll,  color: "bg-amber-400" },
                      { count: stats.approvedAll, color: "bg-emerald-500" },
                      { count: stats.rejectedAll, color: "bg-rose-400" },
                      { count: stats.paidAll,     color: "bg-indigo-500" },
                    ].filter((s) => s.count > 0).map((s, i) => (
                      <div
                        key={i}
                        className={`h-full ${s.color}`}
                        style={{ width: `${(s.count / stats.totalAll) * 100}%` }}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>

            {/* Amount breakdown */}
            <div className="grid grid-cols-3 divide-x">
              <AmountCell label="Pending"  amount={stats?.pendingAmountAll  ?? 0} tone="amber" />
              <AmountCell label="Approved" amount={stats?.approvedAmountAll ?? 0} tone="emerald" />
              <AmountCell label="Total"    amount={stats?.totalAmountAll    ?? 0} tone="slate" />
            </div>
          </div>
        </div>
      )}

      {/* ── Main grid ── */}
      <div className="grid gap-4 lg:grid-cols-12">

        {/* LEFT: Recent claims + Amount summary ── */}
        <div className="space-y-4 lg:col-span-7">

          {/* My recent claims */}
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="flex items-center justify-between border-b bg-slate-50 px-5 py-3">
              <h2 className="font-black text-slate-900">My Recent Claims</h2>
              <Link to="/claims/mine" className="text-xs font-bold text-emerald-700 hover:underline">
                View all →
              </Link>
            </div>
            {myClaims.isLoading ? (
              <p className="px-5 py-6 text-sm text-slate-400">Loading…</p>
            ) : recentMine.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10">
                <Receipt className="h-10 w-10 text-slate-200" />
                <p className="text-sm text-slate-400">No claims submitted yet.</p>
                <Link
                  to="/claims/new"
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-800"
                >
                  <FilePlus2 className="h-3.5 w-3.5" /> New Claim
                </Link>
              </div>
            ) : (
              <div className="divide-y">
                {recentMine.map((c) => (
                  <ClaimRow key={c.id} claim={c} onView={() => navigate(`/claims/${c.id}`)} />
                ))}
              </div>
            )}
          </div>

          {/* My amount summary */}
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-black text-slate-900">My Amount Summary</h2>
            <div className="space-y-2">
              <SummaryRow icon={Clock}        label="Pending"            amount={pendingAmt}  color="text-amber-600" />
              <SummaryRow icon={CheckCircle2} label="Approved"           amount={approvedAmt} color="text-emerald-600" />
              <SummaryRow icon={Wallet}       label="Paid"               amount={paidAmt}     color="text-indigo-600" />
              <SummaryRow icon={XCircle}      label="Rejected"           amount={rejectedAmt} color="text-rose-500" />
              <div className="border-t pt-2">
                <SummaryRow icon={TrendingUp} label="All-time submitted" amount={totalAmt}    color="text-slate-700" bold />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Quick actions + Approval inbox ── */}
        <div className="space-y-4 lg:col-span-5">

          {/* Quick actions */}
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-400">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <ActionTile to="/claims/new"      icon={FilePlus2}     label="New Claim"       accent="emerald" />
              <ActionTile to="/claims/mine"       icon={FileText}      label="My Claims"       badge={stats?.totalMine || undefined} />
              {isApprover && (
                <ActionTile to="/claims/approvals" icon={ClipboardCheck} label="Approval Inbox" badge={inbox.length || undefined} />
              )}
              <ActionTile to="/claims/admin"    icon={ListChecks}    label="All Claims"      badge={stats?.totalAll || undefined} />
              <ActionTile to="/claims/reports"  icon={BarChart2}     label="Reports" />
            </div>
          </div>

          {/* Approval inbox (for approvers) */}
          {isApprover && (
            <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
              <div className="flex items-center justify-between border-b bg-amber-50 px-5 py-3">
                <div>
                  <h2 className="font-black text-amber-900">Pending Approvals</h2>
                  <p className="mt-0.5 text-xs text-amber-700">
                    {approvalInbox.data?.totalElements ?? inbox.length} claim(s) awaiting review
                  </p>
                </div>
                <Link to="/claims/approvals" className="text-xs font-bold text-amber-700 hover:underline">
                  View all →
                </Link>
              </div>
              {approvalInbox.isLoading ? (
                <p className="px-5 py-4 text-sm text-slate-400">Loading…</p>
              ) : recentInbox.length === 0 ? (
                <p className="px-5 py-4 text-sm text-slate-400">No claims awaiting your approval.</p>
              ) : (
                <div className="divide-y">
                  {recentInbox.map((c) => (
                    <ClaimRow key={c.id} claim={c} showEmployee onView={() => navigate(`/claims/${c.id}`)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── sub-components ───────────────────────────────────────────────────────────

function StatChip({
  label, value, tone, to, sub,
}: {
  label: string;
  value: number | null;
  tone: "slate" | "amber" | "emerald" | "indigo";
  to: string;
  sub?: string;
}) {
  const bg  = { slate: "bg-slate-50 border-slate-200", amber: "bg-amber-50 border-amber-100", emerald: "bg-emerald-50 border-emerald-100", indigo: "bg-indigo-50 border-indigo-100" }[tone];
  const num = { slate: "text-slate-700", amber: "text-amber-700", emerald: "text-emerald-700", indigo: "text-indigo-700" }[tone];
  const lbl = { slate: "text-slate-500", amber: "text-amber-600", emerald: "text-emerald-600", indigo: "text-indigo-600" }[tone];
  return (
    <Link to={to} className={`flex flex-col gap-1 rounded-2xl border ${bg} px-4 py-3 shadow-sm transition hover:shadow-md`}>
      <div className={`text-2xl font-black ${num}`}>
        {value === null ? <span className="text-base opacity-40">…</span> : value}
      </div>
      <div className={`text-xs font-bold leading-tight ${lbl}`}>{label}</div>
      {sub && <div className={`text-[10px] font-semibold ${lbl} opacity-70`}>{sub}</div>}
    </Link>
  );
}

function ActionTile({
  to, icon: Icon, label, accent, badge,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  accent?: "emerald";
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className={`relative flex flex-col items-center gap-2 rounded-xl p-3 text-center text-xs font-bold transition-colors ${
        accent === "emerald"
          ? "bg-emerald-700 text-white hover:bg-emerald-800"
          : "border bg-slate-50 text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon className={`h-5 w-5 ${accent === "emerald" ? "text-emerald-100" : "text-emerald-600"}`} />
      <span className="leading-tight">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-black text-white">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </Link>
  );
}

function ClaimRow({
  claim, showEmployee, onView,
}: {
  claim: ExpenseClaimSummary;
  showEmployee?: boolean;
  onView: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-xs font-black text-emerald-700">
        {claim.claimType.charAt(0)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-400">{claim.claimNo}</span>
          <ClaimStatusBadge status={claim.status} />
        </div>
        <div className="mt-0.5 truncate text-sm font-bold text-slate-800">
          {typeLabel(claim.claimType)}
          {showEmployee && claim.employeeName && (
            <span className="ml-1.5 font-normal text-slate-500">· {claim.employeeName}</span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-black text-slate-800">{fmt(claim.totalAmount ?? 0)}</div>
        <button
          onClick={onView}
          className="mt-0.5 text-xs font-bold text-emerald-600 hover:underline"
        >
          View →
        </button>
      </div>
    </div>
  );
}

function SummaryRow({
  icon: Icon, label, amount, color, bold,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  amount: number;
  color: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className={`h-4 w-4 shrink-0 ${color}`} />
      <span className={`flex-1 text-sm ${bold ? "font-bold text-slate-800" : "text-slate-600"}`}>{label}</span>
      <span className={`text-sm font-black ${color}`}>{fmt(amount)}</span>
    </div>
  );
}

function AmountCell({
  label, amount, tone,
}: {
  label: string;
  amount: number;
  tone: "amber" | "emerald" | "slate";
}) {
  const color = { amber: "text-amber-700", emerald: "text-emerald-700", slate: "text-slate-700" }[tone];
  return (
    <div className="px-4 py-3 text-center">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-1 text-sm font-black ${color}`}>{fmt(amount)}</div>
    </div>
  );
}
